/**
 * 💽 용량 계산 — 엑셀 「Capacity」 시트 (v2.233.0)
 *   A. 필요한 디스크 수 — 원하는 용량·디스크 크기·스페어·논리 드라이브 수 → RAID 1·5·6 마다 디스크 수와 인클로저 수
 *   B. 총 사용 가능 용량 — 디스크 크기·총 디스크 수·스페어·논리 드라이브 수 → RAID 1·5·6 마다 실제 쓸 수 있는 용량
 * 엑셀은 A 를 RAID 마다 세 열로 따로 적었는데(값이 달라서), 여기서는 한 번 넣고 세 줄로 본다.
 *
 * v2.313.2 (10-30 · 사용자 「디스크 1개 용량에 24TB 를 넣어줘. 24TB 기준 계산을 제일 많이 쓰거든」): A·B 의 디스크 1개 용량
 *   처음 값을 24TB 로(엑셀 기본값은 A 6TB · B 4TB — 엑셀 대조 검사 check:sizing 은 엑셀 값을 직접 넣어 따로 잰다).
 *   나머지 처음 값(200TB · 16개 · 스페어 0 · LD 1)은 엑셀 그대로.
 */
import { useState } from 'react';
import { calcDrivesNeeded, calcUsableCapacity } from '../../lib/sizing/calc';
import { DISK_RESERVED_MB, LD_META_MB, VIRT_RATIO, driveTbToRealMb } from '../../lib/sizing/data';
import { Field, Formula, NumInput, PAIR_GRID, Panel, SimpleTable, Stat, StatGrid, fmt } from './SizingUi';

const RAID_LABEL = { RAID1: 'RAID 1', RAID5: 'RAID 5', RAID6: 'RAID 6' } as const;

export function CapacityCalc() {
  const [a, setA] = useState({ usableTb: 200, driveTb: 24, spare: 0, logicalDrives: 1 });
  const [b, setB] = useState({ driveTb: 24, totalDrives: 16, spare: 0, logicalDrives: 1, reservedMb: DISK_RESERVED_MB, metaMb: LD_META_MB, virtPct: VIRT_RATIO * 100 });
  const needed = calcDrivesNeeded(a);
  const usable = calcUsableCapacity({ ...b, virtRatio: b.virtPct / 100 });
  const realMbA = driveTbToRealMb(a.driveTb);
  const realMbB = driveTbToRealMb(b.driveTb);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="ui-modal-title mb-1">A. 필요한 디스크 수</h2>
        <p className="text-sm text-slate-500 mb-3">「이만큼 쓰고 싶다」에서 출발 — 그 용량을 내려면 디스크가 몇 개, 인클로저가 몇 대 필요한지.</p>
        <div className={PAIR_GRID}>
          <Panel title="입력" tone="input">
            <Field label="필요한 사용 가능 용량" hint="실제로 쓸 수 있어야 하는 크기 · 1TB = 1,048,576MB"><NumInput value={a.usableTb} min={0} step={1} onChange={(v) => setA({ ...a, usableTb: v })} suffix="TB" /></Field>
            <Field label="디스크 1개 용량" hint={`제조사 표기 · 실제 ${fmt(realMbA)} MB`}><NumInput value={a.driveTb} min={0.1} step={0.1} onChange={(v) => setA({ ...a, driveTb: v })} suffix="TB" /></Field>
            <Field label="스페어 디스크 수"><NumInput value={a.spare} min={0} step={1} onChange={(v) => setA({ ...a, spare: v })} suffix="개" /></Field>
            <Field label="논리 드라이브(LD) 수" hint="RAID 묶음을 몇 개로 나누나"><NumInput value={a.logicalDrives} min={1} step={1} onChange={(v) => setA({ ...a, logicalDrives: v })} suffix="개" /></Field>
          </Panel>
          <Panel title="결과 — RAID 레벨별 필요한 디스크 · 인클로저 수" tone="result">
            <SimpleTable
              head={['RAID', '디스크 수', '12베이', '16베이', '24베이', '48베이', '60베이']}
              align={['l', 'r', 'r', 'r', 'r', 'r', 'r']}
              rows={needed.map((r) => [
                <b key="r">{RAID_LABEL[r.raid]}</b>, <b key="d" className="text-base">{fmt(r.drives)}개</b>,
                `${r.enclosures[12]}대`, `${r.enclosures[16]}대`, `${r.enclosures[24]}대`, `${r.enclosures[48]}대`, `${r.enclosures[60]}대`,
              ])}
            />
            <Formula>
              <p>기본 수 = 필요 용량(MB) ÷ (디스크 실제 MB − 예약 256MB) ÷ (1 − 0.2%)</p>
              <p>RAID 6 = 올림(기본 수 + LD 수 × 2 + 스페어) · RAID 5 = 올림(기본 수 + LD 수 × 1 + 스페어) · RAID 1 = 올림(기본 수 × 2 + 스페어)</p>
              <p>인클로저 수 = 올림(디스크 수 ÷ 베이 수). 필요 용량 1TB = 1,048,576MB · 디스크 1TB = 1,000⁴ 바이트 = 953,674MB (제조사 표기).</p>
            </Formula>
          </Panel>
        </div>
      </div>

      <div>
        <h2 className="ui-modal-title mb-1">B. 총 사용 가능 용량</h2>
        <p className="text-sm text-slate-500 mb-3">「디스크가 이만큼 있다」에서 출발 — RAID 를 짜면 실제로 얼마나 쓸 수 있는지.</p>
        <div className={PAIR_GRID}>
          <Panel title="입력" tone="input">
            <Field label="디스크 1개 용량" hint={`실제 ${fmt(realMbB)} MB`}><NumInput value={b.driveTb} min={0.1} step={0.1} onChange={(v) => setB({ ...b, driveTb: v })} suffix="TB" /></Field>
            <Field label="총 디스크 수"><NumInput value={b.totalDrives} min={1} step={1} onChange={(v) => setB({ ...b, totalDrives: v })} suffix="개" /></Field>
            <Field label="스페어 디스크 수"><NumInput value={b.spare} min={0} step={1} onChange={(v) => setB({ ...b, spare: v })} suffix="개" /></Field>
            <Field label="논리 드라이브(LD) 수"><NumInput value={b.logicalDrives} min={1} step={1} onChange={(v) => setB({ ...b, logicalDrives: v })} suffix="개" /></Field>
            <details className="mt-2">
              <summary className="text-sm text-slate-500 cursor-pointer">고급 — 엑셀이 쓰는 상수 (보통 그대로 둔다)</summary>
              <div className="mt-1">
                <Field label="디스크마다 예약 공간"><NumInput value={b.reservedMb} min={0} step={1} onChange={(v) => setB({ ...b, reservedMb: v })} suffix="MB" /></Field>
                <Field label="LD 마다 메타데이터"><NumInput value={b.metaMb} min={0} step={1} onChange={(v) => setB({ ...b, metaMb: v })} suffix="MB" /></Field>
                <Field label="가상화(볼륨 관리) 몫"><NumInput value={b.virtPct} min={0} max={100} step={0.1} onChange={(v) => setB({ ...b, virtPct: v })} suffix="%" /></Field>
              </div>
            </details>
          </Panel>
          <Panel title="결과 — RAID 레벨별 사용 가능 용량" tone="result">
            <StatGrid>
              {usable.map((u) => (
                <Stat key={u.raid} label={RAID_LABEL[u.raid]} value={u.usableTb > 0 ? fmt(u.usableTb, 2) : '0'} unit="TB" sub={`${fmt(u.usableMb)} MB`} big />
              ))}
            </StatGrid>
            {usable.some((u) => u.usableTb <= 0) && <p className="text-sm text-red-600 mt-3">디스크 수가 너무 적어 그 RAID 를 만들 수 없습니다 (LD 마다 RAID 5 는 2개 이상 · RAID 6 은 3개 이상이 있어야 합니다).</p>}
            <Formula>
              <p>LD 하나의 디스크 수 = (총 디스크 − 스페어) ÷ LD 수</p>
              <p>RAID 1 = ((실제 MB − 예약) × (LD 디스크 수 ÷ 2) − 메타) × (1 − 가상화) × LD 수</p>
              <p>RAID 5 = ((실제 MB − 예약) × (LD 디스크 수 − 1) − 메타) × … · RAID 6 = … (LD 디스크 수 − 2) …</p>
              <p>TB 로 바꿀 때는 ÷ 1,048,576.</p>
            </Formula>
          </Panel>
        </div>
      </div>
    </div>
  );
}
